import React from 'react';
import { Button } from '../../../shared/components/ui';
import { formatCurrency } from '../../../shared/utils/formatters';

function MetricBar({ label, value, weight, color = 'blue' }) {
  const percentage = (value * 100).toFixed(0);
  const weightPercentage = (weight * 100).toFixed(0);

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
  };

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">({weightPercentage}% weight)</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{percentage}%</span>
        </div>
      </div>
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color] || colorClasses.blue}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export function SimilarityBreakdownModal({ neighbor, sourceItem, onClose }) {
  const breakdown = neighbor.similarity_breakdown || {};

  // Calculate weighted contributions
  const semanticScore = breakdown.semantic || 0;
  const semanticWeight = 0.4;

  const globalScore = breakdown.location_similarity || 0;
  const globalWeight = 0.15;

  const categorySpecific = breakdown.category_specific || {};
  const categoryScore = Object.values(categorySpecific).reduce((sum, val) => sum + val, 0) /
    (Object.keys(categorySpecific).length || 1);
  const categoryWeight = 0.45;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Similarity Analysis</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{neighbor.project_name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Overall Score */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Overall Similarity Score</h3>
              <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {(neighbor.similarity_score * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Based on semantic similarity, project characteristics, and category-specific factors
            </p>
          </div>

          {/* Comparison Details */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Line Item Comparison</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="border border-gray-200 dark:border-gray-800 rounded p-3">
                <p className="text-gray-500 dark:text-gray-400 mb-1">Source Item</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">{sourceItem.description}</p>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Quantity:</span>
                    <span className="font-medium">{sourceItem.quantity} {sourceItem.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Unit Cost:</span>
                    <span className="font-medium">{formatCurrency(sourceItem.unitCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Total:</span>
                    <span className="font-medium">{formatCurrency(sourceItem.totalCost)}</span>
                  </div>
                </div>
              </div>
              <div className="border border-blue-200 dark:border-blue-800 rounded p-3 bg-blue-50 dark:bg-blue-900/30">
                <p className="text-blue-600 dark:text-blue-400 mb-1">Similar Item</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">{neighbor.description}</p>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Quantity:</span>
                    <span className="font-medium">{neighbor.quantity} {neighbor.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Unit Cost:</span>
                    <span className="font-medium">{formatCurrency(neighbor.unit_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Total:</span>
                    <span className="font-medium">{formatCurrency(neighbor.total_cost)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similarity Breakdown */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Similarity Score Breakdown</h4>

            {/* Semantic Similarity */}
            <MetricBar
              label="Semantic Similarity"
              value={semanticScore}
              weight={semanticWeight}
              color="blue"
            />
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 ml-1">
              Description and context match based on natural language understanding
            </p>

            {/* Global Factors */}
            <MetricBar
              label="Project Characteristics"
              value={globalScore}
              weight={globalWeight}
              color="green"
            />
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-4 ml-1">
              <div className="grid grid-cols-2 gap-2">
                {breakdown.location_similarity && (
                  <div>Location: {(breakdown.location_similarity * 100).toFixed(0)}%</div>
                )}
                {breakdown.date_recency && (
                  <div>Date recency: {(breakdown.date_recency * 100).toFixed(0)}%</div>
                )}
                {breakdown.size_match && (
                  <div>Size match: {(breakdown.size_match * 100).toFixed(0)}%</div>
                )}
              </div>
            </div>

            {/* Category-Specific Factors */}
            {Object.keys(categorySpecific).length > 0 && (
              <>
                <MetricBar
                  label="Category-Specific Factors"
                  value={categoryScore}
                  weight={categoryWeight}
                  color="purple"
                />
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-4 ml-1">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(categorySpecific).map(([key, value]) => (
                      <div key={key}>
                        {key.replace(/_/g, ' ')}: {(value * 100).toFixed(0)}%
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Cost Variance Analysis */}
          {sourceItem.unitCost && neighbor.unit_cost && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Cost Variance Analysis</h4>
              <div className="space-y-2 text-sm">
                {(() => {
                  const variance = ((neighbor.unit_cost - sourceItem.unitCost) / sourceItem.unitCost) * 100;
                  const isHigher = variance > 0;
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Cost Difference:</span>
                        <span className={`font-medium ${isHigher ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {isHigher ? '+' : ''}{variance.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Absolute Difference:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(Math.abs(neighbor.unit_cost - sourceItem.unitCost))}
                        </span>
                      </div>
                      {neighbor.cost_variance && (
                        <p className="text-gray-600 dark:text-gray-300 mt-2">
                          {isHigher
                            ? 'This similar item was more expensive. Consider reviewing for cost optimization opportunities.'
                            : 'This similar item was less expensive. Your pricing may be competitive or this could indicate a different scope.'}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Project Context */}
          {neighbor.estimate_id && (
            <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800 pt-4">
              <p className="mb-1">
                <span className="font-medium">Source Project:</span> {neighbor.project_name}
              </p>
              {neighbor.project_date && (
                <p>
                  <span className="font-medium">Date:</span> {neighbor.project_date}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimilarityBreakdownModal;
